import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid2,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    ChangeEvent,
    createContext,
    Fragment,
    FunctionComponent,
    PropsWithChildren,
    useContext,
    useEffect,
    useState,
} from 'react';
import { DateRangePicker } from '@/components/DatePicker/DateRangePicker';
import { DatePicker } from '@starjack/shared-components';
import { TimePicker } from '@/components/DatePicker/TimePicker';
import { Select } from '@starjack/shared-components';
import { ProductConfiguration, ProductType } from '@/models/product';
import { DateRange } from '@/models/general';
import { SectionCard } from './SectionCard';
import { ProductAiInfoSection } from './ProductAiInfoSection';
import { isDevelopment } from '@/constants';
import { NumberInput } from '@/components/NumberInput/NumberInput';
import { BookingOptionTabs } from '@/pages/productConfiguration/components/ProductConfigurationDetail/BookingOptionTabs';
import { ConditionLinkAutocomplete } from '@/components/ConditionLinkAutocomplete/ConditionLinkAutocomplete';
import { ProductConfigurationDetailsDialog } from '@/components/ProductConfigurationDetailsDialog/ProductConfigurationDetailsDialog';
import { FilterTagChipSelect } from '@/components/TagChipSelect/FilterTagChipSelect';
import { TagChipSelect } from '@/components/TagChipSelect/TagChipSelect';
import { useGetFilterTagsQuery } from '@/api/v2/filterTags';
import { useGetTagsQuery } from '@/api/v2/tags';
import { useAuthentication } from '@/hooks/useAuthentication';

type ProductConfigurationProps = {
    baseConfiguration: ProductConfiguration;
};

export const ProductConfigurationGeneralConfiguration: FunctionComponent<
    ProductConfigurationProps
> = ({ baseConfiguration }) => {
    // liftOperatorData is null until the stored session is rehydrated, see FilterTagsPage.
    const { liftOperatorData } = useAuthentication();
    const liftOperatorId = liftOperatorData?.liftOperatorId;
    const [productConfiguration, setProductConfiguration] =
        useState<ProductConfiguration>({ ...baseConfiguration });

    const { data: availableFilterTags = [] } = useGetFilterTagsQuery(
        { liftOperatorId },
        { skip: !liftOperatorId }
    );
    const { data: availableTags = [] } = useGetTagsQuery(
        { liftOperatorId },
        { skip: !liftOperatorId }
    );

    const onDateRangeChange =
        (configField: keyof ProductConfiguration) => (dateRange: DateRange) => {
            const [from, to] = dateRange;

            setProductConfiguration((prevState) => ({
                ...prevState,
                [configField]: {
                    from,
                    to,
                },
            }));
        };

    const onDateFieldChange =
        (configField: keyof ProductConfiguration) =>
        (nextValue: Date | null) => {
            setProductConfiguration((prevState) => ({
                ...prevState,
                [configField]: nextValue,
            }));
        };

    const onNumberFieldChange =
        (configField: keyof ProductConfiguration) =>
        (nexValue: number | null) => {
            setProductConfiguration((prevState) => ({
                ...prevState,
                [configField]: nexValue,
            }));
        };

    const onProductTypeOverrideChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;

        setProductConfiguration((prevState) => ({
            ...prevState,
            productTypeOverride: value === '-' ? null : (value as ProductType),
        }));
    };

    const onTextFieldChange =
        (
            configField: keyof ProductConfiguration,
            asStringArray = false,
            maxLength = -1
        ) =>
        (e: ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;

            let nextValue = value;
            if (maxLength > -1) {
                if (value.length > maxLength) {
                    nextValue = value.substring(0, maxLength);
                }
            }

            const processedValue: string | string[] = asStringArray
                ? nextValue
                    ? nextValue.split('\n')
                    : []
                : nextValue;

            setProductConfiguration((prevState) => ({
                ...prevState,
                [configField]: processedValue,
            }));
        };

    useEffect(() => {
        setProductConfiguration({ ...baseConfiguration });
    }, [baseConfiguration]);

    return (
        <GeneralConfigurationContextProvider
            productConfiguration={productConfiguration}
        >
            <Stack spacing={2}>
                <SectionCard title='Allgemeine Daten'>
                    <Box
                        sx={{
                            maxWidth: 400,
                        }}
                    >
                        <ProductConfigurationDetailsDialog
                            shortProductId={baseConfiguration.productId}
                        />
                    </Box>

                    <Grid2 spacing={6} container>
                        <Grid2
                            size={{
                                md: 6,
                                xs: 12,
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    width: '100%',
                                    mb: 2,
                                }}
                                control={
                                    <Checkbox
                                        checked={productConfiguration.enabled}
                                        onChange={(_, checked) =>
                                            setProductConfiguration(
                                                (prevState) => ({
                                                    ...prevState,
                                                    enabled: checked,
                                                })
                                            )
                                        }
                                    />
                                }
                                label='Produkt für Verkauf aktivieren (alle Buchungskanäle)'
                            />
                        </Grid2>

                        <Grid2
                            size={{
                                md: 6,
                                xs: 12,
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    width: '100%',
                                    mb: 2,
                                }}
                                control={
                                    <Checkbox
                                        checked={
                                            productConfiguration.disableStartDateToday
                                        }
                                        onChange={(_, checked) =>
                                            setProductConfiguration(
                                                (prevState) => ({
                                                    ...prevState,
                                                    disableStartDateToday:
                                                        checked,
                                                })
                                            )
                                        }
                                    />
                                }
                                label='Buchung für Heute unterbinden'
                            />

                            <TimePicker
                                label='Buchung für heute sperren ab Uhrzeit'
                                value={productConfiguration.bookingCutoffTime}
                                onChange={(bookingCutoffTime) =>
                                    setProductConfiguration((prevState) => ({
                                        ...prevState,
                                        bookingCutoffTime,
                                    }))
                                }
                                disabled={
                                    productConfiguration.disableStartDateToday
                                }
                                textFieldProps={{
                                    sx: { mb: 2 },
                                    helperText:
                                        productConfiguration.disableStartDateToday
                                            ? 'Nicht wirksam, solange "Buchung für Heute unterbinden" aktiv ist. Die ganztägige Sperre hat Vorrang.'
                                            : 'Leer lassen = keine Sperre. Ab dieser Uhrzeit (österreichische Zeit) ist das Produkt für den heutigen Tag nicht mehr buchbar, für morgen weiterhin.',
                                }}
                            />
                        </Grid2>

                        <Grid2
                            size={{
                                md: 6,
                                xs: 12,
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    width: '100%',
                                }}
                                control={
                                    <Checkbox
                                        checked={
                                            productConfiguration.allowZeroPrice
                                        }
                                        onChange={(_, checked) =>
                                            setProductConfiguration(
                                                (prevState) => ({
                                                    ...prevState,
                                                    allowZeroPrice: checked,
                                                })
                                            )
                                        }
                                    />
                                }
                                label='Tickets mit 0 Euro zulassen'
                            />
                        </Grid2>

                        <Grid2
                            size={{
                                md: 6,
                                xs: 12,
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    width: '100%',
                                }}
                                control={
                                    <Checkbox
                                        checked={
                                            productConfiguration.skipFaceValidation
                                        }
                                        onChange={(_, checked) =>
                                            setProductConfiguration(
                                                (prevState) => ({
                                                    ...prevState,
                                                    skipFaceValidation: checked,
                                                })
                                            )
                                        }
                                    />
                                }
                                label='Gesichtsprüfung überspringen'
                            />
                        </Grid2>

                        <Grid2
                            size={{
                                md: 6,
                                xs: 12,
                            }}
                        >
                            <FormControlLabel
                                sx={{
                                    width: '100%',
                                }}
                                control={
                                    <Checkbox
                                        checked={
                                            !productConfiguration.birthdayRequired
                                        }
                                        onChange={(_, checked) =>
                                            setProductConfiguration(
                                                (prevState) => ({
                                                    ...prevState,
                                                    birthdayRequired: !checked,
                                                })
                                            )
                                        }
                                    />
                                }
                                label='keine Geburtsdatumspflicht'
                            />
                        </Grid2>

                        <Grid2
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                            }}
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack
                                sx={{
                                    height: '100%',
                                }}
                                spacing={2}
                            >
                                <Typography variant='subtitle1' gutterBottom>
                                    Verkaufszeitraum festlegen (alle
                                    Buchungskanäle)
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    <Typography>
                                        In welchem Zeitraum soll das Ticket im
                                        Webshop zum Kauf angeboten werden?
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '0.9rem',
                                            mt: 1,
                                        }}
                                    >
                                        Wenn von SKIDATA / AXESS ein früheres
                                        Enddatum an uns übermittelt wird, so
                                        greift dieses Datum
                                    </Typography>
                                </Box>

                                <DateRangePicker
                                    value={[
                                        productConfiguration.validityRange.from,
                                        productConfiguration.validityRange.to,
                                    ]}
                                    onChange={onDateRangeChange(
                                        'validityRange'
                                    )}
                                    fromText='von'
                                    toText='bis'
                                />
                            </Stack>
                        </Grid2>
                        <Grid2
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                            }}
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack
                                sx={{
                                    height: '100%',
                                }}
                                spacing={2}
                            >
                                <Typography variant='subtitle1' gutterBottom>
                                    Gültigkeitszeitraum des Tickets festlegen
                                    (alle Buchungskanäle)
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    <Typography>
                                        Für welchen Zeitraum kann das Ticket
                                        gebucht und genutzt werden?
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '0.9rem',
                                            mt: 1,
                                        }}
                                    >
                                        Wenn von SKIDATA / AXESS ein früheres
                                        Enddatum an uns übermittelt wird als
                                        hier angegeben, so greift das früher
                                        endende Datum.
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: '500',
                                            fontSize: '0.9rem',
                                            mt: 1,
                                        }}
                                    >
                                        Wichtig: es wird immer der
                                        Gültigkeitsstarttag herangezogen.
                                    </Typography>
                                </Box>

                                <DateRangePicker
                                    value={[
                                        productConfiguration
                                            .startDateValidityRange.from,
                                        productConfiguration
                                            .startDateValidityRange.to,
                                    ]}
                                    onChange={onDateRangeChange(
                                        'startDateValidityRange'
                                    )}
                                    fromText='von'
                                    toText='bis'
                                />
                            </Stack>
                        </Grid2>
                    </Grid2>
                    <Grid2 spacing={6} container>
                        <Grid2
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                            }}
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack
                                sx={{
                                    height: '100%',
                                }}
                                spacing={2}
                            >
                                <Typography variant='subtitle1' gutterBottom>
                                    Startdatum (optional) - für Saison- und
                                    Jahreskarten
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    <Typography>
                                        Startdatum, das angezeigt werden soll.
                                    </Typography>
                                </Box>

                                <DatePicker
                                    label='Startdatum'
                                    value={
                                        productConfiguration.startDateLabel ||
                                        null
                                    }
                                    onChange={onDateFieldChange(
                                        'startDateLabel'
                                    )}
                                />
                            </Stack>
                        </Grid2>
                    </Grid2>
                    <Grid2 spacing={6} container>
                        <Grid2
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1,
                            }}
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack
                                sx={{
                                    height: '100%',
                                }}
                                spacing={2}
                            >
                                <Typography variant='subtitle1' gutterBottom>
                                    Enddatum (optional) - für Saison- und
                                    Jahreskarten
                                </Typography>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    <Typography>
                                        Enddatum z.B. das Saisonende.
                                    </Typography>
                                </Box>

                                <DatePicker
                                    label='Enddatum'
                                    value={productConfiguration.endDate || null}
                                    onChange={onDateFieldChange('endDate')}
                                />

                                <FormControlLabel
                                    sx={{
                                        width: '100%',
                                        mb: 2,
                                    }}
                                    control={
                                        <Checkbox
                                            checked={
                                                productConfiguration.displayEndDate
                                            }
                                            onChange={(_, checked) =>
                                                setProductConfiguration(
                                                    (prevState) => ({
                                                        ...prevState,
                                                        displayEndDate: checked,
                                                    })
                                                )
                                            }
                                        />
                                    }
                                    label='Gültigkeitsenddatum auf Tickets und Buchungsbestätigungen aktivieren (gilt für alle aktivierten Buchungsoptionen)'
                                />
                            </Stack>
                        </Grid2>
                    </Grid2>
                </SectionCard>

                <SectionCard title='Erweiterte Einstellungen'>
                    <Grid2 spacing={6} container>
                        <Grid2
                            size={{
                                md: 12,
                            }}
                        >
                            <Stack
                                sx={{
                                    width: '50%',
                                }}
                                spacing={2}
                            >
                                <Typography variant='subtitle1' gutterBottom>
                                    Regelwerke auswählen
                                </Typography>

                                <ConditionLinkAutocomplete
                                    value={
                                        productConfiguration.conditionLinkIds ||
                                        []
                                    }
                                    onChange={(conditionLinkIds) => {
                                        setProductConfiguration(
                                            (prevState) => ({
                                                ...prevState,
                                                conditionLinkIds,
                                            })
                                        );
                                    }}
                                />
                            </Stack>
                        </Grid2>
                        <Grid2
                            size={{
                                md: 12,
                            }}
                        >
                            <Stack
                                sx={{
                                    width: '50%',
                                }}
                                spacing={4}
                            >
                                <Stack spacing={2}>
                                    <Typography
                                        variant='subtitle1'
                                        gutterBottom
                                    >
                                        Produktkategorie des Tickets ändern
                                    </Typography>

                                    <Select
                                        onChange={onProductTypeOverrideChange}
                                        value={
                                            productConfiguration.productTypeOverride ||
                                            '-'
                                        }
                                        label='Produktkategorie wählen'
                                    >
                                        <option value='-'>
                                            Ziel-Kategorie wählen
                                        </option>
                                        <option value={ProductType.DAY}>
                                            Tages- & / Mehrtagesticket{' '}
                                        </option>
                                        <option value={ProductType.SEASON}>
                                            Saison - & / Jahresticket
                                        </option>
                                    </Select>
                                </Stack>

                                {productConfiguration.productTypeOverride ===
                                    ProductType.DAY && (
                                    <Fragment>
                                        <Typography>
                                            Bei Ziel-Kategorie „Tages- &
                                            Mehrtagestickets“ ist hier
                                            anzugeben, unter welcher Anzahl Tage
                                            (=Dauer) das Ticket im Shop
                                            ausgegeben werden darf.
                                        </Typography>
                                        <NumberInput
                                            onChange={onNumberFieldChange(
                                                'durationOverride'
                                            )}
                                            value={
                                                productConfiguration.durationOverride
                                            }
                                            label='Anzahl Tage'
                                        />
                                    </Fragment>
                                )}
                            </Stack>
                        </Grid2>

                        <Grid2
                            size={{
                                md: 12,
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Sortierschlüssel
                                </Typography>

                                <Typography>
                                    Der Sortierschlüssel bestimmt in welcher
                                    Reihenfolge die Produkte dem Endkunden
                                    angezeigt werden. Der Standardwert ist 0.
                                </Typography>
                                <NumberInput
                                    onChange={onNumberFieldChange('sortKey')}
                                    value={productConfiguration.sortKey}
                                    label='Sortierschlüssel'
                                />
                            </Stack>
                        </Grid2>

                        <Grid2
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Freigabegruppe für Spezialtarif hinterlegen
                                    (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Hier den exakten Namen der Freigabegruppe
                                    eintragen. Damit wird dieses Ticket im
                                    Webshop nur noch für die Gäste sichtbar und
                                    buchbar, die einen Freigabe-Code dieser
                                    Gruppe erhalten. (Mehr dazu im Menü
                                    &quot;Spezialtarife&quot;). Bei Eingabe
                                    mehrerer Freigabe-Gruppen: Bitte je Zeile
                                    einen „Namen der Freigabegruppe“, ohne
                                    Komma, eingeben
                                </Typography>

                                <TextField
                                    label='Bezeichnung der Tarifgruppe hinterlegen'
                                    value={productConfiguration.keycardCodes?.join(
                                        '\n'
                                    )}
                                    onChange={onTextFieldChange(
                                        'keycardCodes',
                                        true
                                    )}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid2>

                        <Grid2
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack
                                sx={{
                                    height: '100%',
                                }}
                                spacing={2}
                            >
                                <Typography variant='subtitle1' gutterBottom>
                                    Tags für Standard-Webshop hinterlegen
                                </Typography>

                                <Typography
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    Bei Übergabe der hier hinterlegten „Tags“
                                    bei der Shop-URL werden nur die Angebote
                                    angezeigt, die sich hinter dem Tag / den
                                    Tags befinden. Tags zentral verwaltbar unter
                                    „Tags verwalten“.
                                </Typography>

                                <TagChipSelect
                                    value={productConfiguration.tags.map(
                                        (t) => t.id
                                    )}
                                    onChange={(ids, createdTag) => {
                                        setProductConfiguration((prev) => {
                                            const known = createdTag
                                                ? [...availableTags, createdTag]
                                                : availableTags;

                                            return {
                                                ...prev,
                                                tags: ids
                                                    .map(
                                                        (id) =>
                                                            known.find(
                                                                (t) =>
                                                                    t.id === id
                                                            ) ??
                                                            prev.tags.find(
                                                                (t) =>
                                                                    t.id === id
                                                            )
                                                    )
                                                    .filter(
                                                        (
                                                            t
                                                        ): t is NonNullable<
                                                            typeof t
                                                        > => t != null
                                                    ),
                                            };
                                        });
                                    }}
                                    label='Tags'
                                />
                            </Stack>
                        </Grid2>
                    </Grid2>
                </SectionCard>

                <SectionCard title='Ticketinfos und Filter'>
                    <Grid2 spacing={6} container>
                        <Grid2
                            size={{
                                xs: 12,
                                md: 6,
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Infofeld DEUTSCH (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Die hier hinterlegten Infos werden in Form
                                    eines Info-Buttons bei diesem Ticket
                                    eingeblendet, welche der Gast mir Klick
                                    darauf öffnet.
                                </Typography>

                                <TextField
                                    label='Infofeld DEUTSCH (alle Buchungskanäle)'
                                    onChange={onTextFieldChange('infoTextDE')}
                                    value={productConfiguration.infoTextDE}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid2>
                        <Grid2
                            size={{
                                xs: 12,
                                md: 6,
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Infofeld ENGLISCH (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Die hier hinterlegten Infos werden in Form
                                    eines Info-Buttons bei diesem Ticket
                                    eingeblendet, welche der Gast mir Klick
                                    darauf öffnet.
                                </Typography>

                                <TextField
                                    label='Infofeld ENGLISCH (alle Buchungskanäle)'
                                    onChange={onTextFieldChange('infoTextEN')}
                                    value={productConfiguration.infoTextEN}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Ticket-Filter (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Filtertags werden im Webshop als
                                    Einzelauswahl-Filter angezeigt, womit sich
                                    der Gast besser zum Wunsch-Ticket führen
                                    lässt. Ein Filtertag kann bei mehreren
                                    Tickets hinterlegt sein und wird unter
                                    „Filtertags verwalten“ zentral gepflegt
                                    (Sprachen, Reihenfolge im Shop).
                                </Typography>

                                <FilterTagChipSelect
                                    value={productConfiguration.filterTags.map(
                                        (t) => t.id
                                    )}
                                    onChange={(ids, createdFilterTag) => {
                                        setProductConfiguration((prev) => {
                                            const known = createdFilterTag
                                                ? [
                                                      ...availableFilterTags,
                                                      createdFilterTag,
                                                  ]
                                                : availableFilterTags;

                                            return {
                                                ...prev,
                                                filterTags: ids
                                                    .map(
                                                        (id) =>
                                                            known.find(
                                                                (t) =>
                                                                    t.id === id
                                                            ) ??
                                                            prev.filterTags.find(
                                                                (t) =>
                                                                    t.id === id
                                                            )
                                                    )
                                                    .filter(
                                                        (
                                                            t
                                                        ): t is NonNullable<
                                                            typeof t
                                                        > => t != null
                                                    ),
                                            };
                                        });
                                    }}
                                    label='Filtertags zuweisen'
                                />
                            </Stack>
                        </Grid2>
                    </Grid2>
                </SectionCard>

                <SectionCard title='PDF-Infos für Pick-up-Voucher / Barcode-Ticket'>
                    <Grid2 spacing={6} container>
                        <Grid2
                            size={{
                                xs: 12,
                                md: 6,
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Zusatzinfo DEUTSCH Abholtickets
                                    (=Pick-up-Voucher)
                                </Typography>

                                <Typography>
                                    Die hier hinterlegten Informationen werden
                                    auf das Abholticket (=Pick-up-Voucher)
                                    aufgedruckt.
                                </Typography>

                                <TextField
                                    label='Zusatzinfo DEUTSCH Abholtickets (=Pick-up-Voucher) - max. 125 Zeichen'
                                    onChange={onTextFieldChange(
                                        'pickupInfoTextDE',
                                        false,
                                        125
                                    )}
                                    value={
                                        productConfiguration.pickupInfoTextDE
                                    }
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid2>
                        <Grid2
                            size={{
                                xs: 12,
                                md: 6,
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Zusatzinfo ENGLISCH Abholtickets
                                    (=Pick-up-Voucher)
                                </Typography>

                                <Typography>
                                    Die hier hinterlegten Informationen werden
                                    auf das Abholticket (=Pick-up-Voucher)
                                    aufgedruckt.
                                </Typography>

                                <TextField
                                    label='Zusatzinfo ENGLISCH Abholtickets (=Pick-up-Voucher) - max. 125 Zeichen'
                                    onChange={onTextFieldChange(
                                        'pickupInfoTextEN',
                                        false,
                                        125
                                    )}
                                    value={
                                        productConfiguration.pickupInfoTextEN
                                    }
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid2>

                        <Grid2
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Zusatzinfo DEUTSCH Barcodetickets
                                    (=e-Ticket)
                                </Typography>

                                <Typography>
                                    Die hier hinterlegten Informationen werden
                                    auf das Barcodeticket (=e-Ticket)
                                    aufgedruckt.
                                </Typography>

                                <TextField
                                    label='Zusatzinfo DEUTSCH Barcodetickets (=e-Ticket) - max. 125 Zeichen'
                                    value={
                                        productConfiguration.barcodeInfoTextDE
                                    }
                                    onChange={onTextFieldChange(
                                        'barcodeInfoTextDE',
                                        false,
                                        125
                                    )}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid2>

                        <Grid2
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Zusatzinfo ENGLISCH Barcodetickets
                                    (=e-Ticket)
                                </Typography>

                                <Typography>
                                    Die hier hinterlegten Informationen werden
                                    auf das Barcodeticket (=e-Ticket)
                                    aufgedruckt.
                                </Typography>

                                <TextField
                                    label='Zusatzinfo ENGLISCH Barcodetickets (=e-Ticket) - max. 125 Zeichen'
                                    value={
                                        productConfiguration.barcodeInfoTextEN
                                    }
                                    onChange={onTextFieldChange(
                                        'barcodeInfoTextEN',
                                        false,
                                        125
                                    )}
                                    rows={5}
                                    multiline
                                />
                            </Stack>
                        </Grid2>
                    </Grid2>
                </SectionCard>

                <SectionCard title='Kontingent'>
                    <Grid2 spacing={6} container>
                        <Grid2
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack spacing={2}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Ticket-Kontingent (alle Buchungskanäle)
                                </Typography>

                                <Typography>
                                    Maximales Kontingent für dieses Ticket pro
                                    Startdatum. Bei Mehrtagestickets wird das
                                    Kontingent an jedem Tag gezählt.
                                    Stornierungen werden dem Kontingent wieder
                                    gutgeschrieben. Hinweis: Ein
                                    Gesamtkontingent pro Startdatum lässt sich
                                    über das starjack-Team hinterlegen!
                                </Typography>

                                <NumberInput
                                    onChange={onNumberFieldChange(
                                        'maxTicketsPerStartDay'
                                    )}
                                    value={
                                        productConfiguration.maxTicketsPerStartDay
                                    }
                                    label='Ticketkontingent'
                                />
                            </Stack>
                        </Grid2>

                        <Grid2
                            size={{
                                md: 6,
                            }}
                        >
                            <Stack
                                sx={{
                                    height: '100%',
                                }}
                                spacing={2}
                            >
                                <Typography variant='subtitle1' gutterBottom>
                                    Kontingent-Schwellenwert
                                </Typography>

                                <Typography
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexGrow: 1,
                                    }}
                                >
                                    Ab welchem Kontingent-Restwert soll das
                                    Kontingent im Webshop angezeigt werden? Wenn
                                    kein Wert hinterlegt ist, wird das gesamte
                                    Kontingent ab Aktivierung angezeigt.
                                </Typography>

                                <NumberInput
                                    onChange={onNumberFieldChange(
                                        'contingentsThreshold'
                                    )}
                                    value={
                                        productConfiguration.contingentsThreshold
                                    }
                                    label='Kontingent-Schwellenwert'
                                />
                            </Stack>
                        </Grid2>
                    </Grid2>
                </SectionCard>

                {/*<SectionCard*/}
                {/*    title='Erklärung der Buchungs-Optionen für die Onlinetickets'*/}
                {/*    hideActions*/}
                {/*>*/}
                {/*    <Typography variant='subtitle1' gutterBottom>*/}
                {/*        Als Standard-Buchungsoption ist die Ticketbuchung auf*/}
                {/*        Keycards („Keycard“) aktiviert. Optional können*/}
                {/*        Unternehmen mit SKIDATA-Zutrittssystem ihren Gästen hier*/}
                {/*        weitere Buchungsoptionen aktivieren. Bitte stellen Sie*/}
                {/*        vorab sicher, dass die technischen Voraussetzungen*/}
                {/*        gegeben sind und dass auch alle Kassa-Mitarbeiter über*/}
                {/*        ggf. anfallende Aufgaben (Ticket Erstellungen / Versand*/}
                {/*        etc.) informiert*/}
                {/*    </Typography>*/}

                {/*    <BookingChannelTabs />*/}
                {/*</SectionCard>*/}

                <SectionCard title='Aktivierung der Buchungs-Optionen für die Onlinetickets nach Verkaufskanal'>
                    <Typography variant='subtitle1' gutterBottom>
                        Hier die Buchungsoptionen für dieses Ticket definieren.
                        Nach dem Speichern sind die Optionen sofort im
                        jeweiligen Verkaufskanal verfügbar.
                    </Typography>
                    <BookingOptionTabs
                        productConfiguration={productConfiguration}
                        onProductConfigurationChange={(newConfiguration) => {
                            setProductConfiguration(newConfiguration);
                        }}
                    />
                </SectionCard>
                {/* Assistant is not released yet: staging and review environments only. */}
                {isDevelopment && (
                    <ProductAiInfoSection
                        key={baseConfiguration.productId}
                        productId={baseConfiguration.productId}
                    />
                )}
            </Stack>
        </GeneralConfigurationContextProvider>
    );
};

export type GeneralConfigurationContextType = {
    productConfiguration: ProductConfiguration;
};

const GeneralConfigurationContext =
    createContext<GeneralConfigurationContextType>({
        productConfiguration: null,
    });
const GeneralConfigurationContextProvider: FunctionComponent<
    PropsWithChildren<{
        productConfiguration: ProductConfiguration;
    }>
> = ({ productConfiguration, children }) => {
    return (
        <GeneralConfigurationContext.Provider
            value={{ productConfiguration: productConfiguration }}
        >
            {children}
        </GeneralConfigurationContext.Provider>
    );
};

export const useGeneralProductConfigurationContext = () => {
    return useContext(GeneralConfigurationContext);
};
